import debug from 'debug';

import { getPayments, getSalesDocuments } from 'services/ErplyAPI';
import { Payment } from 'types/Payment';
import { printPatchscript } from 'actions/integrations/printer';
import { PosPlugin } from 'plugins/plugin';
import { doClientRequest } from 'services/ErplyAPI/core/ErplyAPI2';
import { getTgfConfiguration } from 'plugins/TGF/configuration/Configuration';

const baseLog = debug('plugin:tgf-customizations');

const DEFAULT_PRINT_COUNT = 1;

/**
 * A single rule. Defines a type and value to match with, and a printCount to assign if the rule matches
 *
 * Types available:
 * * documentType: Matches if the salesDocument type is equal to the provided value
 * * paymentType: Matches if the type of *any* payment matches the value
 * * always: Always matches, use to customize the default number of copies or to temporarily disable later rules
 */
type PrintingCountRule =
  | {
      type: 'documentType';
      value: string;
      printCount: number;
    }
  | {
      type: 'paymentType';
      value: string;
      printCount: number;
    }
  | {
      type: 'always';
      printCount: number;
    };

/**
 * Rules that determine how many copies of a receipt should print. The first matching rule wins.
 * If no rule matches, then the default count is {@link DEFAULT_PRINT_COUNT}
 */
export type PrintingCountRules = PrintingCountRule[];

/**
 * A promise that immediately rejects if anyone tries to read it
 * Placeholder for promises that we assume are never read
 */
const unusedPromise = Promise.reject(new Error('This should never happen'));
// Catch the error so we don't get it reported as an 'uncaught exception'
// Anyone trying to read the promise will still receive the error tho
unusedPromise.catch(() => undefined);

// Scenarios given in reverse priority order (from less specific to more specific / last one wins)
// Scenario 1
//   2 copies of the receipt must automatically print when the sales document type is receipt.
//   In this case one copy is kept by the store, the other copy is handed to the customer.
// Scenario 2
//   3 copies of the receipt must automatically print when the sales document type is credit invoice.
//   In this case one copy is kept by the store, one copy is handed to the customer, the last copy is attached to the item being returned
// Scenario 3
//   3 copies of the receipt must automatically print if a transaction is paid by a payment of type "EXCHANGE" or some financing custom payment types.

const defaultPrintingCountRules: PrintingCountRules = [
  { printCount: 3, type: 'paymentType', value: 'EXCHANGE' }, // sc 3
  { printCount: 3, type: 'documentType', value: 'CREDITINVOICE' }, // sc 2
  { printCount: 2, type: 'documentType', value: 'CASHINVOICE' }, // sc 1
  { printCount: 1, type: 'always' },
];

/*
  There are different tradeoffs that can be made here. One could either
  optimize for speed by starting every request that might possibly be needed,
    but this would waste bandwidth
  Or one could optimize for bandwidth by only sending requests once it's certain they are needed,
    but this would waste time because requests are started later than they could be.

  For example, a max speed / parallelism solution might look like

                       (salesdoc type determined print count, payments not necessary)
 onPrintReceipt:       -------|
 getReceiptPrints:     [###]
 getSalesDocuments:    [######]
 getPayments:          [##########]

                       (Reprint, neither salesdoc nor payments necessary)
 onPrintReceipt:       ----|
 getReceiptPrints:     [###]
 getSalesDocuments:    [######]
 getPayments:          [##########]

                       (salesdoc rule did not match, all requests necessary)
 onPrintReceipt:       -----------|
 getReceiptPrints:     [###]
 getSalesDocuments:    [######]
 getPayments:          [##########]

  While a min network usage solution might instead look like

                       (salesdoc type determined print count, payments not necessary)
 onPrintReceipt:       ------------|
 getReceiptPrints:     [###]
 getSalesDocuments:         [######]
 getPayments:

                       (Reprint, neither salesdoc nor payments necessary)
 onPrintReceipt:       ----|
 getReceiptPrints:     [###]
 getSalesDocuments:
 getPayments:

                       (salesdoc rule did not match, all requests necessary)
 onPrintReceipt:       ------------------------|
 getReceiptPrints:     [###]
 getSalesDocuments:         [######]
 getPayments:                       [##########]

 Neither solution is obviously* better.
*/

/**
 * Based on additional rules in plugin configuration, print extra copies of the receipt
 *
 * NB: Does not work for actual reports templates. Those don't even trigger onPrintReceipt in the first place
 * @see {Config}
 */
export const duplicateReceiptIfFirstPrint: Required<
  PosPlugin
>['onPrintReceipt']['on'] = (p, ap) => async (dispatch, getState) => {
  // Logging
  const log = baseLog.extend('onPrintReceipt');
  const logWarn = log.extend('');
  logWarn.log = console.warn;

  log('Checking reprint count to know if we should duplicate or not');
  const invoiceID = p.salesDocument.invoiceID ?? p.salesDocument.id;
  const previousPrintCount = await doClientRequest({
    request: 'getPrintsForOneReceipt',
    invoiceID,
  }).then(res => res.records.length);
  if (previousPrintCount > 1) {
    const ppc = previousPrintCount;
    log(`Measured count greater than 1 (${ppc}), not duplicating`);
    return ap;
  }

  log('Loading rules from plugin configuration');
  const { printingCountRules } = getTgfConfiguration(getState());
  const rules = printingCountRules ?? defaultPrintingCountRules;
  if (!printingCountRules) {
    logWarn('No rules configured, using default printing count rules', {
      printingCountRules,
      defaultPrintingCountRules,
    });
  }

  log('Starting necessary requests for rules', rules);
  // Prepare the necessary data to apply the rules
  let salesDocumentType: Promise<string> = unusedPromise;
  let paymentTypes: Promise<string[]> = unusedPromise;

  if (rules.some(rule => rule.type === 'documentType')) {
    log('Some rule depends on sales document type, starting salesDoc request');
    salesDocumentType = getSalesDocuments({
      id: invoiceID,
      getFields: 'type',
    }).then(res => res[0].type);
  }
  if (rules.some(rule => rule.type === 'paymentType')) {
    if (p.payments?.length && p.payments.every(pmt => pmt.type)) {
      log('Some rule depends on payment types, Using params', p.payments);
      paymentTypes = Promise.resolve(p.payments.map(pmt => pmt.type));
    } else {
      log('Some rule depends on payment types, starting payment request');
      paymentTypes = getPayments({
        documentID: invoiceID,
        getFields: 'type',
      }).then((res: Payment[]) => res.map(pmt => pmt.type));
    }
  }

  log('Evaluating rules to figure out the print count');
  // Figure out the count by applying the rules in order
  let count = DEFAULT_PRINT_COUNT;
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    if (rule.type === 'documentType') {
      // eslint-disable-next-line no-await-in-loop
      if (rule.value === (await salesDocumentType)) {
        log('Matched rule', rule);
        count = rule.printCount;
        break;
      }
    } else if (rule.type === 'paymentType') {
      // eslint-disable-next-line no-await-in-loop
      if ((await paymentTypes).includes(rule.value)) {
        log('Matched rule', rule);
        count = rule.printCount;
        break;
      }
    } else if (rule.type === 'always') {
      log('Matched rule', rule);
      count = rule.printCount;
      break;
    } else {
      logWarn('Unknown rule type', rule, 'skipping');
    }
  }

  const extraCount = count - 1;
  log('printing', extraCount, 'extra receipts from plugin');
  for (let i = 0; i < extraCount; i++) {
    // eslint-disable-next-line no-await-in-loop
    dispatch(printPatchscript(await ap.patchScript));
  }
  return ap;
};
